<script lang="ts">
  import { onMount, getContext } from 'svelte';
  import { type ColorMode } from '@squareup/public-web-design-system';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';
  import type { FormField } from '../types';

  // If XMS-FE styles are enabled
  import PhoneNumberField from './PhoneNumberField.svelte';
  import SelectDropdownField from './SelectDropdownField.svelte';
  import TextAreaField from './TextAreaField.svelte';
  import TextField from './TextField.svelte';
  import CheckboxesField from './CheckboxesField.svelte';
  import RadioButtonsField from './RadioButtonsField.svelte';

  // If market styles are enabled
  import MarketTextField from './MarketTextField.svelte';
  import MarketTextAreaField from './MarketTextAreaField.svelte';
  import MarketSelectDropdownField from './MarketSelectDropdownField.svelte';
  import MarketPhoneNumberField from './MarketPhoneNumberField.svelte';
  import TooltipPartial from '$components/common/partials/TooltipPartial/TooltipPartial.svelte';
  import RichTextField from './RichTextField.svelte';

  // If PWDS styles are enabled
  import PwdsVisibleField from './PwdsVisibleField.svelte';

  export let field: FormField;
  export let validateAllParentFields: () => string | null;
  export let localizedCountryNames: Record<string, string>;
  export let areMarketStylesEnabled: boolean = false;
  export let locale: string = '';
  // PWDS specific props
  export let colorMode: ColorMode;
  export let disabled: boolean;

  const isPWDSEnabled = getContext<DesignSystemEnum>('designSystem') === DesignSystemEnum.PublicWebDesignSystem;

  let tooltip: TooltipPartial;
  let isError: boolean = false;
  // TODO: explore typing this after Svelte 5 branch is merged
  let inputValue: any;
  let hasMounted: boolean = false;

  // The useless equality check is just a way to force this to run any time
  // inputValue changes: https://svelte.dev/tutorial/reactive-declarations
  //
  // hasMounted prevents calling tooltip functions until it's ready
  $: {
    if (inputValue === inputValue && hasMounted) {
      validateAllParentFields();
    }
  }

  const fieldHasError = () => {
    // inputValue is an array for checkboxes, string for everything else
    if (Array.isArray(inputValue)) {
      return field.required && inputValue.length === 0;
    } else {
      // eslint-disable-next-line unicorn/prefer-regexp-test
      return (field.required && !inputValue) || (inputValue && field.pattern && !inputValue.match(field.pattern));
    }
  };

  export const checkForError = () => {
    if (fieldHasError()) {
      isError = true;

      // return the message so the form can display flash banner
      return field.validationMessage;
    }

    isError = false;
    return null;
  };

  export function clearValue() {
    // non-PWDS fields have a use:resetField action instead
    if (isPWDSEnabled) {
      if (field.dataType === 'checkboxes' || (field.dataType === 'select' && field.multiSelect)) {
        inputValue = [];
      } else {
        inputValue = '';
      }
    }
  }

  onMount(() => {
    hasMounted = true;
  });
</script>

{#if isPWDSEnabled}
  <PwdsVisibleField {colorMode} {field} {disabled} {locale} {localizedCountryNames} bind:inputValue bind:isError />
{:else}
  <div class="VisibleField" class:isError class:marketField={areMarketStylesEnabled}>
    {#if field.dataType === 'textArea'}
      {#if areMarketStylesEnabled}
        <MarketTextAreaField {field} bind:inputValue bind:isError />
      {:else}
        <TextAreaField {field} bind:inputValue bind:isError />
      {/if}
    {:else if field.dataType === 'select'}
      {#if areMarketStylesEnabled}
        <MarketSelectDropdownField {field} bind:selectedValue={inputValue} bind:isError />
      {:else}
        <SelectDropdownField {field} bind:selectedValue={inputValue} bind:isError />
      {/if}
    {:else if field.dataType === 'telephone'}
      {#if areMarketStylesEnabled}
        <MarketPhoneNumberField
          {field}
          bind:inputPhoneNumberDigitsOnly={inputValue}
          bind:isError
          {localizedCountryNames}
          {locale}
        />
      {:else}
        <PhoneNumberField
          {field}
          bind:inputPhoneNumberDigitsOnly={inputValue}
          bind:isError
          {localizedCountryNames}
          {locale}
        />
      {/if}
    {:else if field.dataType === 'checkboxes'}
      <CheckboxesField {field} marketField={areMarketStylesEnabled} bind:inputValue bind:isError />
    {:else if field.dataType === 'radioButtons'}
      <RadioButtonsField {field} bind:inputValue bind:isError />
    {:else if field.dataType === 'htmltext'}
      <RichTextField text={field.text} />
    {:else if areMarketStylesEnabled}
      <MarketTextField {field} bind:inputValue bind:isError />
    {:else}
      <TextField {field} bind:inputValue bind:isError />
    {/if}

    <!--Tooltip only needed for fields requiring validation-->
    {#if field.required || field.pattern}
      {#if areMarketStylesEnabled}
        <div class="errorMessage">
          {@html field.validationMessage}
        </div>
      {:else}
        <div class="tooltip">
          <TooltipPartial bind:this={tooltip} message={field.validationMessage ?? undefined} />
        </div>
      {/if}
    {/if}
  </div>
{/if}

<style lang="scss">
  @layer standard-components {
    .VisibleField {
      width: 100%;
      position: relative;
      margin-bottom: var(--space-20);
      padding: 0 var(--space-10) 0 var(--space-10);

      --field-error-message-text-color: #bf0020;
      --field-error-message-text-weight: 400;
      --field-error-message-text-size: 14px;
      --field-error-message-text-leading: 22px;
      --field-error-message-text-tracking: 0;
      --error-icon-height: 16px;
      --error-icon-width: 16px;
      --error-icon-offset: calc(calc(var(--field-error-message-text-leading) - var(--error-icon-height)) / 2);

      .tooltip {
        position: absolute;
        z-index: 1;
        top: 100%;
        width: 100%;
        left: 0;
        padding: 0 var(--space-10) 0 var(--space-10);
        visibility: hidden;
        opacity: 0;
        transition:
          opacity 0.3s cubic-bezier(0, 0, 0.2, 1),
          visibility 0.3s cubic-bezier(0, 0, 0.2, 1);
      }

      .errorMessage {
        display: none;
      }

      &.isError {
        &:hover,
        &:focus-within {
          .tooltip {
            visibility: visible;
            opacity: 1;
          }
        }

        .errorMessage {
          display: block;
          margin-top: 4px;
          color: var(--field-error-message-text-color);
          font-weight: var(--field-error-message-text-weight);
          font-size: var(--field-error-message-text-size);
          line-height: var(--field-error-message-text-leading);
          letter-spacing: var(--field-error-message-text-tracking);
        }
      }

      .errorMessage::before {
        content: '';
        display: inline-block;
        vertical-align: calc(-1 * var(--error-icon-offset));
        width: var(--error-icon-width);
        height: var(--error-icon-height);
        margin-right: 6px;
        margin-left: 2px;
        background-color: var(--field-error-message-text-color);
        mask: url("data:image/svg+xml;utf8,<svg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M8 15.5C12.1333 15.5 15.5 12.1333 15.5 8C15.5 3.86667 12.1333 0.5 8 0.5C3.86667 0.5 0.5 3.86667 0.5 8C0.5 12.1333 3.86667 15.5 8 15.5ZM8 2.16667C11.2167 2.16667 13.8333 4.78333 13.8333 8C13.8333 11.2167 11.2167 13.8333 8 13.8333C4.78333 13.8333 2.16667 11.2167 2.16667 8C2.16667 4.78333 4.78333 2.16667 8 2.16667ZM8 12.1667C8.5753 12.1667 9.04167 11.7003 9.04167 11.125C9.04167 10.5497 8.5753 10.0833 8 10.0833C7.4247 10.0833 6.95833 10.5497 6.95833 11.125C6.95833 11.7003 7.4247 12.1667 8 12.1667ZM7.16667 3.83333H8.83333V9.25H7.16667V3.83333Z'/></svg>");
        -webkit-mask: url("data:image/svg+xml;utf8,<svg width='16' height='16' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M8 15.5C12.1333 15.5 15.5 12.1333 15.5 8C15.5 3.86667 12.1333 0.5 8 0.5C3.86667 0.5 0.5 3.86667 0.5 8C0.5 12.1333 3.86667 15.5 8 15.5ZM8 2.16667C11.2167 2.16667 13.8333 4.78333 13.8333 8C13.8333 11.2167 11.2167 13.8333 8 13.8333C4.78333 13.8333 2.16667 11.2167 2.16667 8C2.16667 4.78333 4.78333 2.16667 8 2.16667ZM8 12.1667C8.5753 12.1667 9.04167 11.7003 9.04167 11.125C9.04167 10.5497 8.5753 10.0833 8 10.0833C7.4247 10.0833 6.95833 10.5497 6.95833 11.125C6.95833 11.7003 7.4247 12.1667 8 12.1667ZM7.16667 3.83333H8.83333V9.25H7.16667V3.83333Z'/></svg>")
          0% 0% / cover;
      }
    }

    :global(.VisibleField label, .VisibleField legend) {
      display: block;
      width: 100%;
      font-weight: var(--font-weight-medium);
      transition:
        color var(--duration-quick) var(--easing-sharp),
        opacity var(--duration-quick) var(--easing-sharp);
    }

    :global {
      .marketInput {
        --field-input-animation-speed: 0.2s;
        --field-input-label-translate: 12px;
        --field-empty-phase-label-text-size-unitless: 16;
        --field-float-phase-label-text-size-unitless: 14;
        --field-size-medium-float-phase-vertical-padding-size: 12px;
        --field-size-medium-float-phase-horizontal-padding-size: 16px;
        --field-size-small-float-phase-vertical-padding-size: 9px;
        --field-size-small-float-phase-horizontal-padding-size: 12px;
        --field-border-radius: 6px;
        --field-border-size: 1px;
        --field-normal-state-background-color: transparent;
        --field-normal-state-input-color: rgba(0, 0, 0, 0.9);
        --field-input-weight: 400;
        --field-input-size: 16px;
        --core-fill-30-dark-mode-color: rgba(0, 0, 0, 0.15);
        --field-float-phase-vertical-padding-size: 8px;
        --field-float-phase-horizontal-padding-size: 16px;
        --field-float-phase-label-text-weight: 600;
        --field-focus-state-float-phase-label-color: rgba(0, 0, 0, 0.9);
        --field-hover-state-normal-validity-border-color: #006aff;
        --field-focus-state-border-size: 2px;
        --field-normal-state-invalid-validity-border-color: #cc0023;
        --field-textarea-height: var(--space-120);
        --field-input-height: 64px;

        padding: var(--field-float-phase-vertical-padding-size) var(--field-float-phase-horizontal-padding-size);
        border-radius: var(--field-border-radius);
        background-color: var(--field-normal-state-background-color);
        color: var(--field-normal-state-input-color);
        position: relative;
        outline: none;
        cursor: text;
        font-weight: var(--field-input-weight);
        font-size: var(--field-input-size);
        line-height: var(--field-input-size);
        display: flex;
        flex-grow: 1;

        &:after {
          content: '';
          position: absolute;
          inset: 0px;
          border-radius: var(--field-border-radius);
          box-shadow: inset 0 0 0 var(--field-border-size) var(--color-gray);
          pointer-events: none;
        }

        &:hover::after {
          box-shadow: inset 0 0 0 var(--field-border-size) var(--field-hover-state-normal-validity-border-color);
        }

        &:focus-within::after {
          box-shadow: inset 0 0 0 var(--field-focus-state-border-size)
            var(--field-hover-state-normal-validity-border-color);
        }

        &.isError::after {
          box-shadow: inset 0 0 0 var(--field-border-size) var(--field-normal-state-invalid-validity-border-color);
        }

        .field-label,
        .arrowIcon,
        label,
        legend,
        input,
        textarea {
          transition: all 0.2s;
          touch-action: manipulation;
        }

        textarea,
        input {
          border: 0;
          font-family: inherit;
          appearance: none;
          border-radius: 0;
          padding: 0;
          cursor: text;
        }

        textarea:focus,
        input:focus {
          outline: 0;
        }

        label,
        legend {
          letter-spacing: 0.05em;
          font-size: var(--field-input-size);
          color: var(--color-gray);
          transform: translateY(var(--field-input-label-translate));
          line-height: 24px;
        }

        input:placeholder-shown + label,
        input:placeholder-shown + legend,
        textarea:placeholder-shown + label {
          cursor: text;
          letter-spacing: 0;
          transform-origin: 0px;
          font-weight: var(--font-weight-regular);
        }

        ::placeholder {
          opacity: 0;
          transition: inherit;
        }

        input:focus::placeholder,
        textarea:focus::placeholder {
          opacity: 1;
        }

        input:not(:placeholder-shown) + label,
        input:not(:placeholder-shown) + legend,
        input:focus + label,
        input:focus + legend,
        textarea:not(:placeholder-shown) + label,
        textarea:focus + label {
          font-weight: var(--field-float-phase-label-text-weight);
          transform: scale(
            calc(var(--field-float-phase-label-text-size-unitless) / var(--field-empty-phase-label-text-size-unitless))
          );
          color: var(--field-focus-state-float-phase-label-color);
        }

        input:not(:placeholder-shown) + label,
        input:not(:placeholder-shown) + legend,
        textarea:not(:placeholder-shown) + label {
          letter-spacing: 0;
          transform-origin: 0;
        }
      }
    }

    .marketField {
      margin-bottom: var(--space-10);
    }
  }
</style>
